import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

// Product-gallery touch tuning. A small intent threshold avoids treating a tap
// or tiny hand movement as a swipe; resistance keeps the live drag feeling
// deliberate before the one-slide release threshold is reached.
const PRODUCT_TOUCH_INTENT_THRESHOLD = 14;
const PRODUCT_TOUCH_SWIPE_THRESHOLD = 56;
const PRODUCT_TOUCH_DRAG_RESISTANCE = 0.72;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('pointerdown', this.#handleMouseDown);
      scroller.removeEventListener('touchstart', this.#handleTouchStart);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isLooping = requestedIndex < 0 || requestedIndex > lastIndex;
    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    const logicalSlide = slides[index];
    let slide = logicalSlide;

    // If jump is more than 1 or we looped, place the destination beside the
    // current slide so the transition only ever travels one slide-width.
    // Product galleries always use an adjacent temporary slide at loop
    // boundaries. The visibility observer can report a peeking edge slide as
    // visible, and relying on `visibleSlides.length === 1` would make one loop
    // direction jump instead of animating normally.
    if (!instant && !isAdjacentSlide && (visibleSlides.length === 1 || (this.#isProductGallery && isLooping))) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      if (!logicalSlide || !currentSlide) {
        this.#disabled = false;
        return;
      }

      if (isLooping) {
        // Animate to a temporary copy at the boundary. This makes the first
        // image appear directly after the last (and vice versa), so removing
        // the copy afterward does not visibly jump the carousel.
        const loopSlide = logicalSlide.cloneNode(true);
        loopSlide.removeAttribute('ref');
        loopSlide.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));
        loopSlide.setAttribute('data-loop-clone', '');
        loopSlide.setAttribute('aria-hidden', 'false');

        if (requestedIndex < current) currentSlide.before(loopSlide);
        else currentSlide.after(loopSlide);

        // Force Safari to calculate the clone's width/offset before the
        // controlled animation starts, especially for the last → first loop.
        void loopSlide.offsetLeft;

        // Inserting before the first slide changes its offset. Re-anchor the
        // current view before animating backward to the temporary copy.
        if (requestedIndex < current) this.#scroll.to(currentSlide, { instant: true });

        slide = loopSlide;

        queueMicrotask(async () => {
          await this.#scroll.finished;

          // Remove the temporary copy while snapping is disabled, then
          // instantly re-anchor to the real logical slide at the same position.
          this.#scroll.snap = false;
          loopSlide.remove();
          this.#scroll.to(logicalSlide, { instant: true });
          this.current = index;
          this.#scroll.snap = true;
          this.#disabled = false;

          // Force Safari to recalculate the timeline state on timeline refresh (after loop)
          requestAnimationFrame(() => {
            this.setAttribute('refreshing-timeline', '');
            requestAnimationFrame(() => {
              this.removeAttribute('refreshing-timeline');
            });
          });
        });
      }
    }

    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    // Keep the logical index while a loop temporarily reorders the DOM. Using
    // indexOf here would report the temporary end/start position instead.
    this.current = index;

    // Keep the thumbnail strip non-looping, but animate its real contents across
    // the boundary so it does not visibly jump from one end to the other.
    if (this.#isProductGallery && isLooping) {
      this.#animateThumbnailBoundary(index);
    } else {
      this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
    }

    // Start the thumbnail rail and main-media transition in the same call
    // stack so both animations begin together.
    if (this.#scroll) {
      this.#scroll.to(slide, { instant, duration: this.#galleryTransitionDuration });
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide: logicalSlide,
        id: logicalSlide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = this.#isProductGallery ? 1 : visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = this.#isProductGallery ? 1 : visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('pointerdown', this.#handleMouseDown);
    scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: false });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    // Ignore observer updates while a temporary loop slide is being animated
    // or removed. Otherwise the real edge slide can win for one frame and
    // snap the carousel back after a completed loop.
    if (this.#disabled || (this.#dragging && this.#isProductGallery)) return;

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    // A touch swipe changes the main scroller directly, bypassing select(), so keep the
    // thumbnail rail in step as well. Only move that rail when the active thumbnail is
    // actually outside its visible viewport; this avoids needless motion on every swipe.
    this.#revealSelectedThumbnail(index, previousIndex);

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;
  #thumbnailAnimationFrame;

  /**
   * Handles touch events on physical phones. Some Safari and Chrome versions
   * dispatch touch events without delivering a complete pointer gesture to a
   * scroller whose position is controlled by JavaScript. This is the only
   * touch path; the pointer handler below ignores product-gallery touch input.
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (!this.#isProductGallery) return;
    if (this.disabled || this.#disabled || this.#dragging) return;
    if (!this.slides || this.slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('model-viewer')) return;
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const startIndex = this.current;
    const controller = new AbortController();
    const { signal } = controller;
    let lastX = startX;
    let lastY = startY;
    let moved = false;
    let horizontal = false;
    let dragOffset = 0;
    let loopClone;
    let loopTargetIndex;
    let loopDirection;

    const removeLoopClone = (index) => {
      if (!loopClone) return;

      this.#scroll.snap = false;
      loopClone.remove();
      const logicalSlide = this.slides?.[index];
      if (logicalSlide) this.#scroll.to(logicalSlide, { instant: true });
      this.current = index;
      loopClone = undefined;
      loopTargetIndex = undefined;
      loopDirection = undefined;
    };

    this.#dragging = true;
    this.pause();

    const finish = async (endEvent) => {
      controller.abort();
      this.#dragging = false;
      this.removeAttribute('dragging');

      if (endEvent.type === 'touchcancel') {
        removeLoopClone(startIndex);
        const cancelledSlide = this.slides?.[startIndex];
        if (cancelledSlide) this.#scroll.to(cancelledSlide, { instant: true });
        this.current = startIndex;
        this.#scroll.snap = true;
        this.#disabled = false;
        this.resume();
        return;
      }

      const changedTouch = endEvent.changedTouches?.[0];
      const endX = changedTouch?.clientX ?? lastX;
      const endY = changedTouch?.clientY ?? lastY;
      const deltaX = startX - endX;
      const deltaY = startY - endY;
      const direction = Math.sign(deltaX);
      const isSwipe =
        moved && horizontal && Math.abs(deltaX) >= PRODUCT_TOUCH_SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY);
      const requestedIndex = isSwipe ? startIndex + direction : startIndex;

      this.current = startIndex;

      // When a boundary clone was created while the finger was moving, settle
      // to that clone directly. Calling select() here would create a second
      // clone and cause the last → first transition to flash or jump.
      if (loopClone) {
        const draggedIntoLoop =
          loopDirection &&
          Math.sign(dragOffset) === loopDirection &&
          Math.abs(dragOffset) >= PRODUCT_TOUCH_SWIPE_THRESHOLD * PRODUCT_TOUCH_DRAG_RESISTANCE;
        const releaseDirection = direction || Math.sign(dragOffset);
        const completeLoop = (isSwipe || draggedIntoLoop) && releaseDirection === loopDirection;
        const targetIndex = completeLoop ? loopTargetIndex : startIndex;
        const targetSlide = completeLoop ? loopClone : this.slides?.[startIndex];

        if (completeLoop) {
          const logicalSlide = this.slides?.[targetIndex];
          this.current = targetIndex;
          this.#animateThumbnailBoundary(targetIndex);

          if (logicalSlide) {
            this.dispatchEvent(
              new SlideshowSelectEvent({
                index: targetIndex,
                previousIndex: startIndex,
                userInitiated: true,
                trigger: 'drag',
                slide: logicalSlide,
                id: logicalSlide.getAttribute('slide-id'),
              })
            );
          }
        }

        if (targetSlide) this.#scroll.to(targetSlide, { duration: this.#galleryTransitionDuration });
        await this.#scroll.finished;
        removeLoopClone(targetIndex);
        this.#scroll.snap = true;
        this.#disabled = false;
        this.resume();
        return;
      }

      if (isSwipe) {
        await this.select(requestedIndex, endEvent, { animate: true });
      } else {
        const currentSlide = this.slides?.[startIndex];
        if (currentSlide) this.#scroll.to(currentSlide, { duration: this.#galleryTransitionDuration });
      }
      await this.#scroll.finished;

      if (this.#dragging) return;
      this.#scroll.snap = true;
      this.resume();
    };

    const move = (moveEvent) => {
      if (moveEvent.touches.length !== 1) return;

      const currentTouch = moveEvent.touches[0];
      const deltaX = startX - currentTouch.clientX;
      const deltaY = startY - currentTouch.clientY;
      const deltaFromLastX = lastX - currentTouch.clientX;
      lastX = currentTouch.clientX;
      lastY = currentTouch.clientY;

      if (!moved && (Math.abs(deltaX) > PRODUCT_TOUCH_INTENT_THRESHOLD || Math.abs(deltaY) > PRODUCT_TOUCH_INTENT_THRESHOLD)) {
        moved = true;
        horizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

        if (horizontal) {
          this.setAttribute('dragging', '');
          this.#scroll.snap = false;
          document.addEventListener('click', preventDefault, { once: true, signal });

          // Put the missing neighbour beside the current slide as soon as a
          // boundary drag becomes horizontal. This lets the user hold the
          // carousel partway through a last → first or first → last swipe.
          const boundaryDirection = Math.sign(deltaX);
          const boundarySlides = this.slides;
          const lastIndex = (boundarySlides?.length ?? 1) - 1;
          const isBoundaryLoop =
            this.infinite &&
            ((startIndex === lastIndex && boundaryDirection > 0) ||
              (startIndex === 0 && boundaryDirection < 0));

          if (isBoundaryLoop && boundarySlides?.[startIndex]) {
            loopTargetIndex = boundaryDirection > 0 ? 0 : lastIndex;
            loopDirection = boundaryDirection;
            loopClone = boundarySlides[loopTargetIndex].cloneNode(true);
            loopClone.removeAttribute('ref');
            loopClone.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));
            loopClone.setAttribute('data-loop-clone', '');
            loopClone.setAttribute('aria-hidden', 'false');

            if (boundaryDirection > 0) boundarySlides[startIndex].after(loopClone);
            else boundarySlides[startIndex].before(loopClone);

            void loopClone.offsetLeft;
            if (boundaryDirection < 0) this.#scroll.to(boundarySlides[startIndex], { instant: true });
            this.#disabled = true;
          }
        }
      }

      if (!horizontal) return;

      // Let the image follow the finger while limiting the live drag to one
      // slide. The release still resolves to exactly one logical neighbour,
      // and select() supplies the controlled easing for the final settle.
      const slideWidth = Math.max(1, this.refs.scroller?.clientWidth || 1);
      const resistedDelta = deltaFromLastX * PRODUCT_TOUCH_DRAG_RESISTANCE;
      const nextDragOffset = clamp(dragOffset + resistedDelta, -slideWidth, slideWidth);
      const appliedDelta = nextDragOffset - dragOffset;
      if (appliedDelta) {
        // Keep the finger drag on one native scroll position. Calling the shared
        // Scroller for every touchmove resets its scroll state on every event,
        // which can produce tiny jumps on iOS Safari and Chrome.
        const scroller = this.refs.scroller;
        scroller.scrollLeft += appliedDelta;
      }
      dragOffset = nextDragOffset;

      if (moveEvent.cancelable) moveEvent.preventDefault();
      moveEvent.stopImmediatePropagation();
    };

    document.addEventListener('touchmove', move, { capture: true, passive: false, signal });
    document.addEventListener('touchend', finish, { capture: true, signal });
    document.addEventListener('touchcancel', finish, { capture: true, signal });
  };

  /**
   * Handles a pointer gesture to start dragging slides. Pointer Events are
   * also the touch path on current iOS Safari and Chrome. Keeping one gesture
   * implementation prevents a physical phone from running both pointer and
   * legacy touch handlers for the same swipe.
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    // Product galleries use the touch event path below. Ignoring touch
    // pointers here prevents a single phone swipe from being consumed twice.
    if (this.#isProductGallery && event.pointerType === 'touch') return;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#disabled || this.#dragging) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Do not cancel the initial touch. The product gallery uses
    // `touch-action: pan-y`, so the browser can still decide that a mostly
    // vertical gesture belongs to page scrolling. Horizontal movement is
    // cancelled once the pointer is captured below.
    if (event.pointerType === 'mouse') event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const getPointerPosition = (pointerEvent) => (axis === 'x' ? pointerEvent.clientX : pointerEvent.clientY);
    const startPosition = getPointerPosition(event);

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let lastPosition = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;
    const dragStartIndex = this.current;
    const isProductGallery = this.#isProductGallery;
    const activePointerId = event.pointerId;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      if (event.pointerId !== activePointerId) return;

      const current = getPointerPosition(event);
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Prevent native touch momentum from selecting additional slides. The
      // product gallery resolves the gesture to exactly one logical step on up.
      if (isProductGallery) event.preventDefault();

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      lastPosition = current;
      distanceTravelled += Math.abs(delta);

      // The product gallery resolves the gesture to one adjacent slide on
      // release. Do not let native scroll momentum or a live scroll position
      // change the direction before that decision is made.
      if (!isProductGallery) this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      if (event.pointerId !== activePointerId) return;

      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      if (isProductGallery) {
        const wasCancelled = event.type === 'pointercancel' || event.type === 'pointercapturelost';

        if (wasCancelled) {
          this.#scroll.to(slides[dragStartIndex], { instant: true });
          this.current = dragStartIndex;
          this.removeAttribute('dragging');
          this.#scroll.snap = true;
          this.resume();
          return;
        }

        const lastIndex = slides.length - 1;
        const eventPosition = getPointerPosition(event);
        const isPositionedEndEvent = event.type === 'pointerup' || event.type === 'pointercancel';
        const endPosition = isPositionedEndEvent && Number.isFinite(eventPosition) ? eventPosition : lastPosition;
        const totalDelta = startPosition - endPosition;
        const direction = Math.sign(totalDelta) || Math.sign(velocity);
        const hasSwipe = moved && direction !== 0 && (Math.abs(totalDelta) >= 10 || distanceTravelled >= 10);
        const requestedIndex = hasSwipe ? dragStartIndex + direction : dragStartIndex;
        const isLooping = requestedIndex < 0 || requestedIndex > lastIndex;
        const newIndex = isLooping
          ? requestedIndex < 0
            ? lastIndex
            : 0
          : clamp(requestedIndex, 0, lastIndex);
        const newSlide = slides[newIndex];

        if (!newSlide) return;

        // A product-gallery swipe is always limited to the adjacent image,
        // regardless of swipe velocity or how far native scrolling travelled.
        // Boundary swipes use select() so the same directional loop transition
        // as the arrow buttons is used.
        this.current = dragStartIndex;
        if (isLooping && this.infinite) {
          await this.select(requestedIndex, event, { animate: true });
        } else {
          this.#scroll.to(newSlide, { duration: this.#galleryTransitionDuration });
          this.current = newIndex;
          this.#centerSelectedThumbnail(newIndex);

          this.dispatchEvent(
            new SlideshowSelectEvent({
              index: newIndex,
              previousIndex: dragStartIndex,
              userInitiated: true,
              trigger: 'drag',
              slide: newSlide,
              id: newSlide.getAttribute('slide-id'),
            })
          );
        }

        this.removeAttribute('dragging');
        if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);

        await this.#scroll.finished;

        // It's possible that the user started dragging again before the scroll finished
        if (this.#dragging) return;

        this.#scroll.snap = true;
        this.resume();
        return;
      }

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide, { duration: this.#galleryTransitionDuration });

      this.removeAttribute('dragging');
      if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * Cancel an active drag if another pointer starts elsewhere. Ignore the
     * pointerdown that started this drag, which bubbles here after the handler
     * above has registered this listener.
     */
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerId !== activePointerId) {
          controller.abort();
          this.#dragging = false;
          this.removeAttribute('dragging');
          this.#scroll.snap = true;
          this.resume();
        }
      },
      { signal }
    );
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Moves the real thumbnail rail across a loop boundary at the same time as
   * the main gallery. The rail itself is not cloned: it smoothly travels from
   * its current end to the opposite end, then lands exactly on the selected
   * thumbnail without a duplicate or a native fast-scroll race.
   * @param {number} index
   */
  #animateThumbnailBoundary(index) {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    const { thumbnailsContainer } = this.refs;
    if (!(selectedThumbnail instanceof HTMLElement) || !(thumbnailsContainer instanceof HTMLElement)) return;

    cancelAnimationFrame(this.#thumbnailAnimationFrame);

    const isVertical = thumbnailsContainer.scrollHeight > thumbnailsContainer.clientHeight;
    const startPosition = isVertical ? thumbnailsContainer.scrollTop : thumbnailsContainer.scrollLeft;
    const containerRect = thumbnailsContainer.getBoundingClientRect();
    const thumbnailRect = selectedThumbnail.getBoundingClientRect();
    const targetPosition = isVertical
      ? startPosition + thumbnailRect.top - containerRect.top - (containerRect.height - thumbnailRect.height) / 2
      : startPosition + thumbnailRect.left - containerRect.left - (containerRect.width - thumbnailRect.width) / 2;
    const maxPosition = isVertical
      ? Math.max(0, thumbnailsContainer.scrollHeight - thumbnailsContainer.clientHeight)
      : Math.max(0, thumbnailsContainer.scrollWidth - thumbnailsContainer.clientWidth);
    const endPosition = clamp(targetPosition, 0, maxPosition);
    const distance = endPosition - startPosition;

    if (Math.abs(distance) < 1) return;

    // Do not allow a stylesheet/native smooth scroll to compete with the
    // frame-by-frame position used for this boundary transition.
    const previousScrollBehavior = thumbnailsContainer.style.scrollBehavior;
    thumbnailsContainer.style.scrollBehavior = 'auto';
    const duration = this.#galleryTransitionDuration ?? 520;
    const startTime = performance.now();

    const animate = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const position = startPosition + distance * easedProgress;

      if (isVertical) thumbnailsContainer.scrollTop = position;
      else thumbnailsContainer.scrollLeft = position;

      if (progress < 1) {
        this.#thumbnailAnimationFrame = requestAnimationFrame(animate);
      } else {
        if (isVertical) thumbnailsContainer.scrollTop = endPosition;
        else thumbnailsContainer.scrollLeft = endPosition;
        thumbnailsContainer.style.scrollBehavior = previousScrollBehavior;
        this.#thumbnailAnimationFrame = undefined;
      }
    };

    this.#thumbnailAnimationFrame = requestAnimationFrame(animate);
  }

  /** Whether this slideshow is the product image carousel. */
  get #isProductGallery() {
    return this.closest('media-gallery')?.dataset.presentation === 'carousel';
  }

  /** A short, controlled settle only for the main product-media carousel. */
  get #galleryTransitionDuration() {
    return this.#isProductGallery ? 360 : undefined;
  }

  /**
   * Brings the active thumbnail back into view after a direct swipe of the main media.
   * @param {number} index
   * @param {number} previousIndex
   */
  #revealSelectedThumbnail(index, previousIndex) {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    const { thumbnailsContainer } = this.refs;
    if (!(selectedThumbnail instanceof HTMLElement) || !(thumbnailsContainer instanceof HTMLElement)) return;

    const thumbnailRect = selectedThumbnail.getBoundingClientRect();
    const containerRect = thumbnailsContainer.getBoundingClientRect();
    const isVertical = thumbnailsContainer.scrollHeight > thumbnailsContainer.clientHeight;
    const isOutside = isVertical
      ? thumbnailRect.top < containerRect.top || thumbnailRect.bottom > containerRect.bottom
      : thumbnailRect.left < containerRect.left || thumbnailRect.right > containerRect.right;

    if (!isOutside) return;

    // Keep the active thumbnail on the edge matching the swipe direction. With five
    // thumbnails visible, image 5 → 6 reveals 2–6; reversing 4 → 3 reveals 3–7.
    const alignment = index < previousIndex ? 'start' : 'end';
    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior: 'smooth',
      block: alignment,
      inline: alignment,
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
